import { useProjectContext } from "@hooks/context/useProjectContext";
import { useSocialProcurement } from "@hooks/crud/useSocialProcurement";
import { PageBreadcrumbs } from "@stories/atoms/PageBreadcrumbs/PageBreadcrumbs";
import { SubTitleWithImage } from "@stories/atoms/SubTitle/SubTitle";
import { useEffect, useState } from "react";
import * as RouteHelper from "@utils/routes";
import { SocialProcurementLineItem } from "@interfaces/socialProcurement";
import { useProfileContext } from "@hooks/context/useProfileContext";
import SocialProcurementModal from "@stories/organisms/SocialProcurementModal/SocialProcurementModal";
import { useParams } from "react-router-dom";
import { downloadFile } from "@hooks/utils/useUpload";
import { SocialProcurementSheet } from "@stories/organisms/SocialProcurementSheet/SocialProcurementSheet";
import { ReportingPageTabs } from "@stories/molecules/ReportingPageTabs/ReportingPageTabs";
import { Loader } from "@stories/atoms/Loaders/Loader/Loader";
import { ProjectLoader } from "@stories/atoms/Loaders/ProjectLoader/ProjectLoader";
import { StyledCard } from "@stories/atoms/StyledCard/StyledCard";
import { Grid } from "@mui/material";
import { StyledButton } from "@stories/atoms/StyledButton/StyledButton";
import PublishIcon from "@mui/icons-material/Publish";
import { MonthSelect } from "@stories/atoms/MonthSelect/MonthSelect";
import {
  ReportStatus,
  ContractorReportLineItem,
} from "@interfaces/contractorReport";
import { useReport } from "@hooks/crud/useReports";

export const SocialProcurements = () => {
  const { reportId } = useParams();
  const { userProfile } = useProfileContext();
  const [selectedSocialProcurement, setSelectedSocialProcurement] = useState<
    SocialProcurementLineItem | undefined
  >(undefined);

  const { selectedProject, selectedProjectOrganisation, contractorPackage } =
    useProjectContext();

  const [selectedReport, setSelectedReport] = useState<
    ContractorReportLineItem | undefined
  >(undefined);

  const { getReport, isReportLoading, updateReport } = useReport();

  useEffect(() => {
    const getSelectedReport = async (reportId: string) => {
      const report = await getReport(reportId);
      setSelectedReport(report);
    };
    if (reportId) {
      getSelectedReport(reportId);
    }
  }, [reportId]);

  const today = new Date();
  const [reportDate, setReportDate] = useState(
    today < selectedProject.endDate ? today : selectedProject.endDate
  );

  const {
    isSocialProcurementLoading,
    socialProcurements,
    createSocialProcurement,
    updateSocialProcurement,
    deleteSocialProcurements,
    upsertSocialProcurements,
  } = useSocialProcurement(undefined, undefined, reportId);

  const [open, setOpen] = useState(false);
  const handleClose = () => {
    setOpen(false);
    setSelectedSocialProcurement(undefined);
  };

  const handleUpsertSocialProcurements = (
    socialProcurements: SocialProcurementLineItem[]
  ) => {
    upsertSocialProcurements(socialProcurements);
  };

  const handleDeleteSocialProcurements = (
    socialProcurements: SocialProcurementLineItem[]
  ) => {
    deleteSocialProcurements(socialProcurements);
  };

  if (!reportId) {
    console.log("Social procurement PAGE LOADER");
    return <ProjectLoader />;
  }
  const breadcrumbs = [
    {
      path: RouteHelper.contractorReports(selectedProject.id),
      name: `${selectedProject.projectName} Reports`,
    },
    {
      path: RouteHelper.socialProcurement(selectedProject.id, reportId),
      name: `${selectedProject.projectName} Spend`,
    },
  ];

  const submitReport = async () => {
    if (selectedReport) {
      const updatedReport = {
        ...selectedReport,
        status: ReportStatus.Complete,
      };
      await updateReport(updatedReport);
      setSelectedReport(updatedReport);
      setOpen(true);
    }
  };

  return (
    <>
      <PageBreadcrumbs
        organisationLogo={selectedProjectOrganisation.organisationLogo}
        title={"Social Procurement"}
        links={breadcrumbs}
      />
      <StyledCard>
        <Grid container spacing={2} sx={{ padding: "24px" }}>
          <Grid item xs={12} md={8}>
            <ReportingPageTabs
              reportDate={reportDate}
              setReportDate={setReportDate}
              // submitReport={submitReport}
              reportId={reportId}
            />
          </Grid>
          <Grid
            item
            xs={12}
            md={2}
            sx={{
              display: "flex",
              alignContent: "right",
              justifyContent: "right",
              margin: "auto",
            }}
            justifyContent={"right"}
          >
            <MonthSelect
              setDate={setReportDate}
              date={reportDate}
              minDate={selectedProject.startDate}
              maxDate={selectedProject.endDate}
            />
          </Grid>

          <Grid
            item
            xs={12}
            md={2}
            sx={{
              display: "flex",
              alignContent: "right",
              justifyContent: "right",
              margin: "auto",
            }}
            justifyContent={"right"}
          >
            {selectedReport ? (
              <StyledButton
                variant="contained"
                color={
                  selectedReport.status === ReportStatus.Complete
                    ? "success"
                    : "secondary"
                }
                startIcon={<PublishIcon />}
                onClick={submitReport}
                disabled={isSocialProcurementLoading || isReportLoading}
              >
                Submit Report
              </StyledButton>
            ) : null}
          </Grid>
          {socialProcurements ? (
            <SocialProcurementSheet
              socialProcurements={socialProcurements}
              projectId={selectedProject.id}
              contractorPackage={contractorPackage}
              reportId={reportId}
              loading={isSocialProcurementLoading}
              handleUpsertSocialProcurements={handleUpsertSocialProcurements}
              handleDeleteSocialProcurements={handleDeleteSocialProcurements}
            />
          ) : (
            <Loader />
          )}
        </Grid>
      </StyledCard>
    </>
  );
};
